/**
 * ServicesStrip — the horizontal band holding multi-day services, stacked into lanes ("subrows"). Each
 * service spans one or more whole days and is positioned with the same per-day width factors as the grid.
 * Empty day slots (and a trailing phantom subrow for creating a brand-new lane) light up as drop targets
 * while a compatible drag is active. Services can be dragged between day/lane, resized across days, or opened.
 */
import { useRef } from 'react';
import type { TFunction } from 'i18next';

import { TYPE_COLOR, TypeIcon } from '../model';
import { readDrag, writeDrag, type PalettePayload } from '../model';
import type { BoardPart } from '../model';
import { cn } from '@/shared/lib/utils';
import { spanLabel } from './stripGeometry';
import { useStripResize } from './useStripResize';

type StyleVars = React.CSSProperties & Record<string, string | number>;

interface ServicesStripProps {
  t: TFunction;
  dayCount: number;
  weights: number[];
  services: BoardPart[];
  readOnly: boolean;
  dropActive: boolean;
  onOpen: (uid: string) => void;
  onDropPalette: (payload: PalettePayload, dayIdx: number, row: number) => void;
  onMove: (uid: string, dayIdx: number, row: number) => void;
  onResize: (uid: string, days: number) => void;
  onDragChange: (dragging: boolean) => void;
}

export function ServicesStrip({
  t,
  dayCount,
  weights,
  services,
  readOnly,
  dropActive,
  onOpen,
  onDropPalette,
  onMove,
  onResize,
  onDragChange,
}: ServicesStripProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const total = weights.reduce((a, b) => a + b, 0);
  const startResize = useStripResize(rowRef, onResize);

  if (dayCount <= 0) return <div className="tp-svc-row" ref={rowRef} />;

  const cum = (d: number) => weights.slice(0, d).reduce((a, b) => a + b, 0);
  const dayPct = (d: number) => (weights[d] / total) * 100;
  const cumPct = (d: number) => (cum(d) / total) * 100;
  const spanPct = (d: number, span: number) =>
    (weights.slice(d, d + span).reduce((a, b) => a + b, 0) / total) * 100;
  const inset = (d: number) => (weights[d] / total / 12) * 100;

  // Lane bookkeeping: render every used lane plus one phantom lane at the bottom for new-lane drops.
  const usedRows = new Set<number>();
  services.forEach((s) => usedRows.add(s.row ?? 0));
  const maxRow = usedRows.size === 0 ? -1 : Math.max(...usedRows);
  const rowIdxs: number[] = [];
  for (let r = 0; r <= maxRow; r += 1) rowIdxs.push(r);
  rowIdxs.push(maxRow + 1); // phantom row for new-row drops

  const handleResizeStart = (
    event: React.MouseEvent,
    service: BoardPart,
    siblings: BoardPart[]
  ) => {
    // Cap the span at the gap to the next service on the same lane (or the trip end).
    const next = siblings.find((o) => o.day > service.day);
    const maxDays = (next ? next.day : dayCount) - service.day;
    startResize(event, service, maxDays, -1);
  };

  const emptySlotStyle = (d: number, isPhantom: boolean): React.CSSProperties => ({
    position: 'absolute',
    top: 6,
    bottom: 6,
    left: `calc(${cumPct(d)}% + ${inset(d)}%)`,
    width: `calc(${dayPct(d)}% - ${inset(d) * 2}%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    borderRadius: 6,
    border: `1px dashed ${dropActive ? 'var(--phx-border-3)' : 'transparent'}`,
    background: isPhantom && dropActive ? 'var(--phx-surface-2)' : 'transparent',
    fontSize: 10.5,
    fontStyle: 'italic',
    color: 'var(--phx-text-muted)',
    textAlign: 'center',
    opacity: dropActive ? 1 : 0,
    pointerEvents: dropActive ? 'auto' : 'none',
    transition: 'opacity .15s',
  });

  const renderEmpty = (d: number, rowIdx: number, isPhantom: boolean) => (
    <div
      key={`svc-empty-${rowIdx}-${d}`}
      style={emptySlotStyle(d, isPhantom)}
      onDragOver={(e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = 'var(--phx-primary)';
      }}
      onDragLeave={(e) => {
        e.currentTarget.style.borderColor = dropActive ? 'var(--phx-border-3)' : 'transparent';
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = dropActive ? 'var(--phx-border-3)' : 'transparent';
        const payload = readDrag(e);
        if (!payload) return;
        if (payload.source === 'svc-strip') onMove(payload.uid, d, rowIdx);
        else if (payload.source === 'palette') onDropPalette(payload, d, rowIdx);
      }}
    >
      {isPhantom ? t('costScheme.newService') : t('costScheme.dropService')}
    </div>
  );

  const renderRow = (rowIdx: number) => {
    const isPhantom = rowIdx > maxRow;
    const inRow = isPhantom ? [] : services.filter((s) => (s.row ?? 0) === rowIdx);
    const sortedRow = [...inRow].sort((a, b) => a.day - b.day);
    const cells: React.ReactNode[] = [];
    let cursor = 0;
    sortedRow.forEach((service) => {
      for (let d = cursor; d < service.day; d += 1) cells.push(renderEmpty(d, rowIdx, false));
      const span = Math.max(1, service.span);
      const insetL = inset(service.day);
      const insetR = inset(service.day + span - 1);
      const style: StyleVars = {
        left: `calc(${cumPct(service.day)}% + ${insetL}%)`,
        width: `calc(${spanPct(service.day, span)}% - ${insetL + insetR}%)`,
        '--act-color': TYPE_COLOR[service.type],
      };
      cells.push(
        <div
          key={`svc-${service.uid}`}
          className="tp-svc-cell"
          draggable={!readOnly}
          onDragStart={(e) => {
            writeDrag(e, { source: 'svc-strip', uid: service.uid }, 'move');
            onDragChange(true);
          }}
          onDragEnd={() => onDragChange(false)}
          style={style}
          onClick={() => onOpen(service.uid)}
          title={`${service.name} · ${spanLabel(t, service.day, span, 'day')}`}
        >
          <div className="tp-act-accent" aria-hidden="true" />
          <div className="tp-act-body">
            <div className="tp-act-icon">
              <TypeIcon type={service.type} transportKind={service.transportKind} size={12} />
            </div>
            <div className="tp-act-text">
              <div className="tp-act-label">{service.name}</div>
              <div className="tp-act-time">{spanLabel(t, service.day, span, 'day')}</div>
            </div>
          </div>
          {readOnly ? null : (
            <div
              className="tp-svc-resize"
              title={t('costScheme.stretchDays')}
              onMouseDown={(e) => handleResizeStart(e, service, sortedRow)}
            />
          )}
        </div>
      );
      cursor = service.day + span;
    });
    for (let d = cursor; d < dayCount; d += 1) cells.push(renderEmpty(d, rowIdx, isPhantom));
    return (
      <div
        key={`svc-row-${rowIdx}`}
        className={cn('tp-svc-subrow', isPhantom && 'tp-svc-subrow--phantom')}
      >
        {cells}
      </div>
    );
  };

  return (
    <div className="tp-svc-row" ref={rowRef}>
      {rowIdxs.map(renderRow)}
    </div>
  );
}
